import { useEffect, useState } from "react";
import { toast } from "sonner";
import { api, errMessage } from "../../lib/api";
import { TIMEZONE_LABEL } from "../../lib/media";
import { Loader, EmptyState } from "../../components/Blocks";

// Generic admin CRUD screen driven by a field config.
// field: { key, label, type: text|textarea|bool|number|csv|select, options }
export default function AdminResource({ resource, title, description, fields, columns, rowAction }) {
  const [items, setItems] = useState(null);
  const [editing, setEditing] = useState(null);

  const load = async () => setItems((await api.get(`/admin/${resource}`)).data);
  useEffect(() => {
    setItems(null);
    load().catch((e) => toast.error(errMessage(e)));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [resource]);

  const blank = () => {
    const obj = {};
    fields.forEach((f) => {
      if (f.type === "datetime") obj[`${f.key}_local`] = "";
      else obj[f.key] = f.type === "bool" ? false : "";
    });
    return obj;
  };

  const toPayload = (form) => {
    const out = {};
    fields.forEach((f) => {
      const v = f.type === "datetime" ? form[`${f.key}_local`] : form[f.key];
      if (f.type === "csv") out[f.key] = String(v || "").split(",").map((s) => s.trim()).filter(Boolean);
      else if (f.type === "bool") out[f.key] = !!v;
      else if (f.type === "number") out[f.key] = v === "" || v == null ? null : Number(v);
      else out[f.key] = v === "" ? null : v;
    });
    return out;
  };

  const fromDoc = (doc) => {
    const form = { ...doc };
    fields.forEach((f) => {
      if (f.type === "csv") form[f.key] = Array.isArray(doc[f.key]) ? doc[f.key].join(", ") : "";
      else if (f.type === "date") form[f.key] = doc[f.key] ? String(doc[f.key]).slice(0, 10) : "";
      else if (f.type === "datetime") form[`${f.key}_local`] = doc[`${f.key}_local`] || "";
      else if (form[f.key] == null) form[f.key] = f.type === "bool" ? false : "";
    });
    return form;
  };

  const save = async (e) => {
    e.preventDefault();
    try {
      const payload = toPayload(editing);
      if (editing.id) await api.patch(`/admin/${resource}/${editing.id}`, payload);
      else await api.post(`/admin/${resource}`, payload);
      toast.success("Enregistré");
      setEditing(null);
      load();
    } catch (err) {
      toast.error(errMessage(err));
    }
  };

  const remove = async (id) => {
    try {
      await api.delete(`/admin/${resource}/${id}`);
      toast.success("Supprimé");
      load();
    } catch (e) {
      toast.error(errMessage(e));
    }
  };

  if (items === null) return <Loader />;

  return (
    <div data-testid={`admin-${resource}`}>
      <div className="flex items-center justify-between flex-wrap gap-4">
        <h1 className="vb-display text-3xl lg:text-4xl font-bold">{title}</h1>
        <button className="vb-btn vb-btn-primary !py-2" data-testid={`admin-${resource}-new`} onClick={() => setEditing(blank())}>
          Nouveau
        </button>
      </div>
      {description && <p className="mt-3 text-sm text-neutral-500 max-w-3xl">{description}</p>}

      {items.length === 0 ? (
        <div className="mt-8">
          <EmptyState message="Aucun enregistrement." testid={`admin-${resource}-empty`} />
        </div>
      ) : (
        <div className="mt-8 overflow-x-auto">
          <table className="w-full text-sm min-w-[800px]" data-testid={`admin-${resource}-table`}>
            <thead>
              <tr className="text-left vb-label border-b border-white/10">
                {columns.map((c) => (
                  <th key={c.key} className="py-3 pr-4">
                    {c.label}
                  </th>
                ))}
                <th className="py-3" />
              </tr>
            </thead>
            <tbody>
              {items.map((it) => (
                <tr key={it.id} className="border-b border-white/5" data-testid={`admin-${resource}-row-${it.id}`}>
                  {columns.map((c) => (
                    <td key={c.key} className="py-3 pr-4 text-neutral-300">
                      {c.format
                        ? c.format(it[c.key], it)
                        : typeof it[c.key] === "boolean"
                        ? it[c.key]
                          ? "Oui"
                          : "Non"
                        : Array.isArray(it[c.key])
                        ? it[c.key].length
                        : String(it[c.key] ?? "—").slice(0, 60)}
                    </td>
                  ))}
                  <td className="py-3 whitespace-nowrap">
                    {rowAction && (
                      <button
                        className="vb-btn vb-btn-ghost !py-1.5 !px-4 !text-xs mr-3"
                        data-testid={`${rowAction.testid}-${it.id}`}
                        onClick={() => rowAction.onClick(it)}
                      >
                        {rowAction.label}
                      </button>
                    )}
                    <button
                      className="vb-btn vb-btn-ghost !py-1.5 !px-4 !text-xs"
                      data-testid={`admin-${resource}-edit-${it.id}`}
                      onClick={() => setEditing(fromDoc(it))}
                    >
                      Éditer
                    </button>
                    <button
                      className="ml-3 text-xs text-[#FF3B30] hover:underline"
                      data-testid={`admin-${resource}-delete-${it.id}`}
                      onClick={() => remove(it.id)}
                    >
                      Supprimer
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {editing && (
        <div className="fixed inset-0 z-[70] bg-black/80 backdrop-blur-sm flex items-start justify-center p-4 overflow-y-auto" data-testid={`admin-${resource}-modal`}>
          <form onSubmit={save} className="bg-[#0a0a0a] border border-white/10 w-full max-w-3xl p-8 my-8">
            <div className="flex items-center justify-between">
              <h2 className="vb-display text-2xl">{editing.id ? "Modifier" : "Créer"}</h2>
              <button type="button" onClick={() => setEditing(null)} data-testid={`admin-${resource}-modal-close`} className="text-neutral-500 hover:text-white">
                ✕
              </button>
            </div>
            <div className="mt-8 grid md:grid-cols-2 gap-6">
              {fields.map((f) => (
                <div key={f.key} className={f.type === "textarea" ? "md:col-span-2" : ""}>
                  <label className="vb-label">{f.label}</label>
                  {f.type === "textarea" ? (
                    <textarea
                      className="vb-input min-h-[90px]"
                      value={editing[f.key] ?? ""}
                      onChange={(e) => setEditing({ ...editing, [f.key]: e.target.value })}
                      data-testid={`admin-${resource}-field-${f.key}`}
                    />
                  ) : f.type === "bool" ? (
                    <button
                      type="button"
                      className="vb-chip mt-2"
                      data-active={!!editing[f.key]}
                      data-testid={`admin-${resource}-field-${f.key}`}
                      onClick={() => setEditing({ ...editing, [f.key]: !editing[f.key] })}
                    >
                      {editing[f.key] ? "Oui" : "Non"}
                    </button>
                  ) : f.type === "select" ? (
                    <select
                      className="vb-input"
                      value={editing[f.key] ?? ""}
                      onChange={(e) => setEditing({ ...editing, [f.key]: e.target.value })}
                      data-testid={`admin-${resource}-field-${f.key}`}
                    >
                      <option value="" className="bg-black">
                        —
                      </option>
                      {(f.options || []).map((o) => (
                        <option key={o} value={o} className="bg-black">
                          {o}
                        </option>
                      ))}
                    </select>
                  ) : f.type === "datetime" ? (
                    <input
                      className="vb-input [color-scheme:dark]"
                      type="datetime-local"
                      value={editing[`${f.key}_local`] ?? ""}
                      onChange={(e) => setEditing({ ...editing, [`${f.key}_local`]: e.target.value })}
                      data-testid={`admin-${resource}-field-${f.key}`}
                    />
                  ) : (
                    <input
                      className="vb-input [color-scheme:dark]"
                      type={f.type === "number" ? "number" : f.type === "date" ? "date" : "text"}
                      value={editing[f.key] ?? ""}
                      onChange={(e) => setEditing({ ...editing, [f.key]: e.target.value })}
                      data-testid={`admin-${resource}-field-${f.key}`}
                      placeholder={f.type === "csv" ? "valeurs séparées par des virgules" : ""}
                    />
                  )}
                  {f.type === "datetime" && (
                    <div className="mt-2 text-[11px] text-neutral-400" data-testid={`admin-${resource}-utc-hint-${f.key}`}>
                      {TIMEZONE_LABEL}
                    </div>
                  )}
                </div>
              ))}
            </div>
            <div className="mt-10 flex gap-4">
              <button type="submit" className="vb-btn vb-btn-primary" data-testid={`admin-${resource}-save`}>
                Enregistrer
              </button>
              <button type="button" className="vb-btn vb-btn-ghost" onClick={() => setEditing(null)} data-testid={`admin-${resource}-cancel`}>
                Annuler
              </button>
            </div>
          </form>
        </div>
      )}
    </div>
  );
}
